import { Koi } from '@ksyos/koi';
import type { CustomControlCode } from '@common/enums';
import type { Field, Section } from '@common/formEngine/formEngine.types';
import { dependsOn } from '@common/formEngine/dependsOn';
import {
    episodesAndJournalDraftAddEntriesSchema,
    episodesAndJournalFinalAddEntriesSchema,
} from '@common/validation/episodesAndJournal';
import { labResultDraftAddSchema, LabResultFinalAddSchema } from '@common/validation/labResult';
import type { ProcessActivityProfessionalSummaryData } from '../../processActivityProfessionalSummaryData';
import type { HolterAnamnesisData, HolterEcgData, HolterHistoryData, MedicationData } from './utils/utils';
import { initialMedicationData, medicationDataDraftSchema, medicationDataFinalSchema } from './utils/medicationUtils';
import {
    RedenHolterEventOption,
    redenHolterEventOptions,
    RelevanteVoorgeschiedenisOption,
    relevanteVoorgeschiedenisOptions,
    relevanteVoorgeschiedenisOptionsWithTextInput,
} from './utils/utils';

export type HolterExaminationRequest =
    | CustomControlCode.HolterHomeExaminationRequest
    | CustomControlCode.HolterDirectExaminationRequest
    | CustomControlCode.HolterExternalExaminationRequest
    | CustomControlCode.HolterZorgdomeinExaminationRequest;

export type HolterRequest =
    | CustomControlCode.HolterHomeExaminationRequest
    | CustomControlCode.HolterDirectExaminationRequest
    | CustomControlCode.HolterExternalExaminationRequest
    | CustomControlCode.HolterZorgdomeinExaminationRequest
    | CustomControlCode.HolterConsultRequest
    | CustomControlCode.HolterFollowUpConsultRequest;

export const holterAnamnesisSection: Section<HolterAnamnesisData> = {
    fields: [
        {
            checkboxEnumType: RedenHolterEventOption,
            isRequired: true,
            key: 'reden_holter_event',
            label: 'Reden voor holter/event',
            type: 'nestedCheckboxGroup',
            valueOptions: redenHolterEventOptions,
        },
        {
            isRequired: true,
            key: 'hasPacemaker',
            label: 'Pacemaker',
            type: 'boolean',
        },
    ],
    title: 'Anamnese',
};

export const holterRustEcgSection: Section<HolterEcgData> = {
    fields: [
        {
            isRequired: true,
            key: 'ecg',
            label: 'ECG gemaakt',
            type: 'boolean',
        },

        {
            include: dependsOn('ecg'),
            isRequired: true,
            key: 'ecg_afwijkingen',
            label: 'Afwijkingen gevonden',
            type: 'boolean',
        },
        {
            include: dependsOn('ecg_afwijkingen'),
            isRequired: true,
            key: 'ecg_afwijkingen_opmerkingen',
            label: 'Gevonden afwijkingen',
            type: 'textArea',
        },
    ],
    title: 'Rust ECG',
};

export const holterHistorySection: Section<HolterHistoryData> = {
    fields: [
        {
            checkboxEnumType: RelevanteVoorgeschiedenisOption,
            hasTextInputOptions: relevanteVoorgeschiedenisOptionsWithTextInput,
            key: 'relevante_voorgeschiedenis',
            label: 'Relevante voorgeschiedenis (cardiovasculair)',
            type: 'checkboxGroup',
            valueOptions: relevanteVoorgeschiedenisOptions,
        },
    ],
    title: 'Voorgeschiedenis',
};

export const medicationSection: Section<MedicationData> = {
    fields: [
        {
            isRequired: true,
            key: 'patientUsesMedication',
            label: 'Patient gebruikt medicatie (cardiovasculair)',
            type: 'boolean',
        },
        {
            draftSchema: medicationDataDraftSchema,
            finalSchema: medicationDataFinalSchema,
            include: dependsOn('patientUsesMedication'),
            initialValue: initialMedicationData,
            isRequired: true,
            key: 'medication',
            label: 'Medicatie',
            type: 'custom',
        },
    ],
    title: 'Medicatie',
};

export const professionalSummarySectionFields: Field<ProcessActivityProfessionalSummaryData>[] = [
    {
        draftSchema: Koi.array().items(labResultDraftAddSchema).optional(),
        finalSchema: Koi.array().items(LabResultFinalAddSchema).optional(),
        initialValue: [],
        key: 'labResults',
        label: '',
        type: 'custom',
    },
    {
        draftSchema: episodesAndJournalDraftAddEntriesSchema.optional(),
        finalSchema: episodesAndJournalFinalAddEntriesSchema.optional(),
        initialValue: { episodes: [], journalEntries: [] },
        key: 'episodesAndJournals',
        label: '',
        type: 'custom',
    },
];
